import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  const d = new Date(date);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800)
    return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return formatDate(date);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
}

export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate GitHub URL format specifically
 */
export function isValidGitHubUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);

    // Check if it's a GitHub URL
    if (urlObj.hostname !== "github.com") {
      return false;
    }

    // Check path format (should be /owner/repo)
    const pathParts = urlObj.pathname.split("/").filter((part) => part);
    if (pathParts.length < 2) {
      return false;
    }

    const owner = pathParts[0];
    const repo = pathParts[1].replace(/\.git$/, "");

    // Basic validation of owner and repo names
    if (!/^[a-zA-Z0-9._-]+$/.test(owner) || !/^[a-zA-Z0-9._-]+$/.test(repo)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Normalize GitHub URL for comparison
 */
export function normalizeGitHubUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/").filter((part) => part);
    const owner = pathParts[0];
    const repo = pathParts[1]?.replace(/\.git$/, "");

    return `https://github.com/${owner}/${repo}`.toLowerCase();
  } catch {
    return url.toLowerCase();
  }
}

export function extractTagsFromText(text: string): string[] {
  const words = text.toLowerCase().split(/\s+/);
  const techWords = [
    "kubernetes",
    "docker",
    "terraform",
    "aws",
    "azure",
    "gcp",
    "helm",
    "ansible",
    "jenkins",
    "gitlab",
    "github",
    "ci/cd",
    "monitoring",
    "logging",
    "security",
    "networking",
    "storage",
    "database",
    "redis",
    "postgresql",
    "mysql",
    "mongodb",
    "microservices",
    "serverless",
    "containers",
    "devops",
  ];

  return techWords.filter((word) =>
    words.some((w) => w.includes(word) || word.includes(w)),
  );
}
